import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { themePreference, type ThemePreference } from './storage';

/**
 * How the app is lit.
 *
 * The palette lives in styles.css as two sets of CSS variables, dark on
 * `:root` and light under `[data-theme='light']`, and every Tailwind color
 * points at them. Switching is therefore one attribute on the document
 * element; nothing in the tree has to know.
 *
 * The preference is one of three: follow the OS, or light, or dark. It is a
 * property of this browser, kept in localStorage, because a person who wants
 * their work laptop light and their bedroom tablet dark is not unusual, and
 * because it has to be readable before React mounts (index.html applies it
 * inline so the first paint is already the right color).
 *
 * A branded share page can pin the page to the team's choice; see
 * `useThemePin`. The pin wins over the preference while it is mounted.
 */

export type Theme = 'light' | 'dark';
export type { ThemePreference };

export const THEME_ATTRIBUTE = 'data-theme';

/** The address bar / status bar color for each theme: the page color. */
const THEME_COLOR: Record<Theme, string> = { dark: '#0B0D10', light: '#F7F8FA' };

const DARK_QUERY = '(prefers-color-scheme: dark)';

function osTheme(): Theme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'dark';
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

export function resolveTheme(preference: ThemePreference, os: Theme): Theme {
  return preference === 'system' ? os : preference;
}

/** Write the theme onto the document. Idempotent; safe to call often. */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (root.getAttribute(THEME_ATTRIBUTE) !== theme) root.setAttribute(THEME_ATTRIBUTE, theme);
  root.style.colorScheme = theme;
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta && meta.content !== THEME_COLOR[theme]) meta.content = THEME_COLOR[theme];
}

interface ThemeContextValue {
  /** What the person asked for. */
  preference: ThemePreference;
  /** What the page is actually showing, after the OS and any pin. */
  theme: Theme;
  setPreference: (next: ThemePreference) => void;
  /** Pin the page to one theme while the caller is mounted. */
  pin: (theme: Theme | null) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => themePreference.get());
  const [os, setOs] = useState<Theme>(osTheme);
  const [pinned, setPinned] = useState<Theme | null>(null);

  // The OS setting, live: macOS switches at sunset and the app should too.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) => setOs(event.matches ? 'dark' : 'light');
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  // Another tab changed the preference: follow it, so two windows of the
  // same app never disagree about what time of day it is.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === themePreference.key) setPreferenceState(themePreference.get());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const theme = pinned ?? resolveTheme(preference, os);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setPreference = useCallback((next: ThemePreference) => {
    themePreference.set(next);
    setPreferenceState(next);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, theme, setPreference, pin: setPinned }),
    [preference, theme, setPreference],
  );

  return createElement(ThemeContext.Provider, { value }, children);
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme must be used inside <ThemeProvider>.');
  return value;
}

/**
 * Pin the whole page to a theme for as long as the caller is mounted.
 *
 * A team that branded its share pages chose how they are lit, and a viewer's
 * own preference does not override a brand any more than it recolors the
 * team's website. Set on the document rather than on a page root so the body
 * behind the page is lit the same way: a dark strip under a white page on
 * overscroll is exactly the kind of detail that makes branding look bolted
 * on. Cleared on the way out, because the next page in this single-page app
 * is not theirs.
 */
export function useThemePin(theme: Theme | null | undefined): void {
  const { pin } = useTheme();
  useEffect(() => {
    if (!theme) return;
    pin(theme);
    return () => pin(null);
  }, [theme, pin]);
}
